<template>
  <RevDrawer
    :back-button-label="i18n(modalBuybackTranslations.backButtonLabel)"
    :close-button-label="i18n(modalBuybackTranslations.close)"
    data-test="modal-swap"
    :name="props.modalName"
    :title="i18n(translations.titleModal)"
    @close="handleClose"
    @open="handleOpen"
  >
    <template #body>
      <Intro
        v-if="displayIntro"
        :close-wording
        :image
        :price="props.price"
        :price-without-trade-in
        :product-title
        :variant="intro"
        @close="handleCloseModal"
        @next-step="getCategories"
      />

      <template v-if="displayFunnel || displayOffer || displayCategories">
        <div
          v-if="!displayCategories"
          class="text-static-brand-mid mb-16 flex items-center"
        >
          <RevButtonBase @click="handleBack">
            <IconArrowLeft
              v-if="
                displayFunnel || displayOffer || (displayCategories && intro)
              "
              :aria-label="i18n(modalBuybackTranslations.backButtonLabel)"
              size="medium"
            />
          </RevButtonBase>
          <h3 class="body-1 m-auto">{{ title }}</h3>
        </div>
        <Categories
          v-if="displayCategories"
          :categories
          class="mt-7"
          @item-selected="handleItemSelected"
          @next-step="getCategoryFunnel"
        />
        <div v-if="displayFunnel" ref="questions-container">
          <QuestionsForm
            :buttonLabel
            class="mb-72"
            :has-submit-button
            :is-loading-offer="isPostingAnswers"
            :questions="activeQuestions"
            @answer="onAnswer"
          />
        </div>

        <OfferStep
          v-if="displayOffer"
          :buybackOffer="offer"
          :buybackOfferPayload="form"
          :close-wording
          :model
          :zone
          @close="handleCloseModal"
          @default-item-selected="goToCategories"
          @item-selected="handleItemSelected"
          @remove="handleRemove"
        />
      </template>
      <div v-if="displayLoader" class="mt-6 flex items-center justify-center">
        <RevLoadingScreen :text="loadingText" />
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { inject, ref, useTemplateRef } from 'vue'

import modalBuybackTranslations from '@backmarket/nuxt-layer-buyback/app/utils/Modal.translations'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevDrawer } from '@ds/components/Drawer'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'

import QuestionsForm from '~/scopes/buyback/components/QuestionsForm/QuestionsForm.vue'
import type { ErrorData } from '~/scopes/buyback/composables/useBuybackErrorBoundary'
import { useSwapModal } from '~/scopes/buyback/composables/useSwapModal'
import Categories from '~/scopes/buyback/swap/components/Categories/Categories.vue'
import Intro from '~/scopes/buyback/swap/components/Intro/Intro.vue'
import OfferStep from '~/scopes/buyback/swap/components/SwapOfferStep/SwapOfferStep.vue'

import type { SwapModalProps } from '../SwapModalContainer/SwapModalContainer.vue'

import translations from './SwapModal.translations'
import { SWAP_INFO_MESSAGE } from './constants'

const i18n = useI18n()
const container = useTemplateRef<HTMLElement>('questions-container')

const emit = defineEmits(['confirmation', 'continue'])

const props = withDefaults(defineProps<SwapModalProps>(), {
  redirectOnCloseTarget: null,
  initialPayload: undefined,
  intro: undefined,
})

const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: SWAP_INFO_MESSAGE.SWAP_EMBEDDED_MODAL,
  step: 'intro',
}

const {
  categories,
  offer,
  isPostingAnswers,
  displayLoader,
  displayIntro,
  displayCategories,
  displayFunnel,
  displayOffer,
  loadingText,
  title,
  closeWording,
  buttonLabel,
  model,
  activeQuestions,
  hasSubmitButton,
  form,
  handleBack,
  handleClose,
  handleOpen,
  handleCloseModal,
  handleRemove,
  handleItemSelected,
  getCategoryFunnel,
  getCategories,
  goToCategories,
  onAnswer: onAnswerBase,
} = useSwapModal(props, emit)

async function onAnswer(key: string, value: string | string[] | false) {
  await onAnswerBase(key, value)

  if (container.value) {
    container.value.scrollIntoView(true)
  }
}
</script>
